import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { IPOIData } from '@/types/poi-management.types';
import { useIntl } from 'react-intl';

interface OpeningHoursProps {
  openingHours?: string;
  setFormData: React.Dispatch<React.SetStateAction<Partial<IPOIData>>>;
  errors: { [key: string]: string };
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  disabled: boolean;
}

interface DayHours {
  open?: string;
  close?: string;
}

const OpeningHours: React.FC<OpeningHoursProps> = ({
  openingHours,
  setFormData,
  errors,
  setErrors,
  disabled,
}) => {
  const intl = useIntl();
  const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  const initialHours: Record<string, DayHours> = {};

  const [hours, setHours] = useState<Record<string, DayHours>>(() => {
    if (!openingHours || openingHours === '') {
      return initialHours;
    }
    if (typeof openingHours === 'string') {
      try {
        return JSON.parse(openingHours);
      } catch (e) {
        console.error('Error parsing opening hours:', e);
        return initialHours;
      }
    }
    return openingHours || initialHours;
  });

  useEffect(() => {
    if (openingHours && openingHours !== '') {
      try {
        const parsedHours =
          typeof openingHours === 'string'
            ? JSON.parse(openingHours)
            : openingHours;

        const validHours = Object.keys(parsedHours).reduce(
          (acc, day) => {
            if (parsedHours[day].open && parsedHours[day].close) {
              acc[day] = parsedHours[day];
            }
            return acc;
          },
          {} as Record<string, DayHours>
        );

        setHours(validHours);
      } catch (e) {
        console.error('Error parsing opening hours:', e);
        setHours(initialHours);
      }
    } else {
      setHours(initialHours);
    }
  }, [openingHours]);

  const handleToggleDay = (day: string, isOpen: boolean) => {
    let updatedHours: Record<string, DayHours>;
    if (isOpen) {
      updatedHours = {
        ...hours,
        [day]: { open: '08:00', close: '18:00' },
      };
    } else {
      updatedHours = { ...hours };
      delete updatedHours[day];
    }
    setHours(updatedHours);
    setFormData((prev) => ({
      ...prev,
      opening_hours: JSON.stringify(updatedHours),
    }));
    setErrors((prev) => {
      const rest = { ...prev };
      delete (rest as Record<string, string>).opening_hours;
      return rest;
    });
  };

  const handleTimeChange = (
    day: string,
    field: 'open' | 'close',
    value: string
  ) => {
    const updatedHours = {
      ...hours,
      [day]: {
        ...hours[day],
        [field]: value,
      },
    };
    setHours(updatedHours);
    setFormData((prev) => ({
      ...prev,
      opening_hours: JSON.stringify(updatedHours),
    }));
    setErrors((prev) => {
      const rest = { ...prev } as Record<string, string>;
      delete rest.opening_hours;
      return rest;
    });
  };

  return (
    <div className="space-y-4">
      {daysOfWeek.map((day) => {
        const isOpen =
          day in hours && 'open' in hours[day] && 'close' in hours[day];
        const dayTranslationKey = `POI.OPENING_HOURS.${day.toUpperCase()}`;
        const dayName = intl.formatMessage({ id: dayTranslationKey });

        return (
          <div key={day} className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 w-24">
                {dayName}
              </span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isOpen}
                  onChange={(e) => handleToggleDay(day, e.target.checked)}
                  disabled={disabled}
                  className={clsx(
                    'h-5 w-5 text-blue-500 cursor-pointer border-gray-200 rounded focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
                    disabled && 'bg-gray-100 cursor-not-allowed'
                  )}
                  aria-label={`Toggle ${dayName} opening hours`}
                />
                <span className="text-gray-700 cursor-pointer">
                  {isOpen
                    ? intl.formatMessage({ id: 'POI.OPENING_HOURS.OPEN' })
                    : intl.formatMessage({ id: 'POI.OPENING_HOURS.CLOSED' })}
                </span>
              </label>
            </div>
            {isOpen && (
              <div className="flex items-center gap-3 ml-28">
                <input
                  type="time"
                  value={hours[day].open || ''}
                  onChange={(e) =>
                    handleTimeChange(day, 'open', e.target.value)
                  }
                  disabled={disabled}
                  className={clsx(
                    'input input-bordered w-32 h-10 rounded-lg border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
                    disabled && 'bg-gray-100 cursor-not-allowed',
                    errors.opening_hours && 'border-red-500'
                  )}
                  aria-label={`${dayName} opening time`}
                  autoComplete="off"
                />
                <span className="text-gray-700">
                  {intl.formatMessage({ id: 'POI.OPENING_HOURS.TO' })}
                </span>
                <input
                  type="time"
                  value={hours[day].close || ''}
                  onChange={(e) =>
                    handleTimeChange(day, 'close', e.target.value)
                  }
                  disabled={disabled}
                  className={clsx(
                    'input input-bordered w-32 h-10 rounded-lg border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
                    disabled && 'bg-gray-100 cursor-not-allowed',
                    errors.opening_hours && 'border-red-500'
                  )}
                  aria-label={`${dayName} closing time`}
                  autoComplete="off"
                />
              </div>
            )}
          </div>
        );
      })}
      {errors.opening_hours && (
        <p
          className="text-red-500 text-xs mt-1"
          role="alert"
          id="opening_hours-error"
        >
          {errors.opening_hours}
        </p>
      )}
    </div>
  );
};

export default OpeningHours;
